import { BadRequestError } from '@sparelabs/error-types'
import { Logger } from '@sparelabs/logging'
import { RandomClass } from '@sparelabs/random'
import turfArea from '@turf/area'
import turfBearing from '@turf/bearing'
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import buffer from '@turf/buffer'
import centroid from '@turf/centroid'
import circle from '@turf/circle'
import turfDestination from '@turf/destination'
import turfDistance from '@turf/distance'
import flatten from '@turf/flatten'
import length from '@turf/length'
import lineSlice from '@turf/line-slice'
import nearestPointOnLine from '@turf/nearest-point-on-line'
import pointToLineDistance from '@turf/point-to-line-distance'
import { polygonToLine } from '@turf/polygon-to-line'
import { randomPoint } from '@turf/random'
import simplify from '@turf/simplify'
import truncate from '@turf/truncate'
import * as turf from '@turf/turf'
import { booleanEqual, difference } from '@turf/turf'
import union from '@turf/union'
import deepEqual from 'deep-equal'
import { BBox, Feature, MultiPolygon, Polygon } from 'geojson'
import { isEqual } from 'lodash'
import ngeohash from 'ngeohash'
// @ts-ignore
import extent from 'turf-extent'
import {
  GeoJsonCoordinate,
  GeometryType,
  IGeometry,
  ILineString,
  IMultiPoint,
  IMultiPolygon,
  IPoint,
  IPolygon,
} from './GeographyTypes'
// eslint-disable-next-line import/no-cycle
import { buildPoint } from './GeometryBuilder'

const TURF_UNITS = 'meters'
const MINIMUM_SIMPLIFIED_GEOMETRY_SIZE: number = 1024
const SIMPLIFICATION_TOLERANCE = 0.0001

/**
 * We use 4326 which is the SRID for the WGS84 spatial reference system, a global standard for geography.
 * This is especially useful when dealing with geo data and operations such as calculating
 * distances between points or determining whether a point lies within a certain area.
 */
export const GEOMETRY_SRID: number = 4326

interface INearestPointResult {
  closestPoint: IPoint
  index: number
  distance: number
}

export interface IRandomPointOptions {
  finenessInMeters: number
  random: RandomClass
}

export class Geography {
  private static readonly logger = Logger.build(Geography.name)

  public static area(polygon: IPolygon): number {
    return turfArea(polygon)
  }

  // Crow flies distance, in meters, between 2 points
  public static distance(point1: IPoint, point2: IPoint): number {
    return Math.round(turfDistance(point1, point2, { units: TURF_UNITS }))
  }

  public static distanceToPolygon(point: IPoint, polygon: IPolygon): number {
    return this.inside(point, polygon)
      ? 0
      : Math.min(
          ...flatten(polygonToLine(polygon)).features.map((feature) =>
            pointToLineDistance(point, feature.geometry, { units: TURF_UNITS }),
          ),
        )
  }

  public static inside(point: IPoint, polygon: IPolygon) {
    return booleanPointInPolygon(point, polygon)
  }

  public static isPointValid(point: IPoint): boolean {
    return this.areCoordinatesValid(point.coordinates)
  }

  public static isMultiPointValid(multiPoint: IMultiPoint): boolean {
    return multiPoint.coordinates.every((coordinates) => this.areCoordinatesValid(coordinates))
  }

  public static isLineStringValid(lineString: ILineString): boolean {
    return lineString.coordinates.every((coordinates) => this.areCoordinatesValid(coordinates))
  }

  public static multiPointToPointArray(multiPoint: IMultiPoint): IPoint[] {
    return multiPoint.coordinates.map((coordinates) => buildPoint(...coordinates))
  }

  public static pointArrayToMultiPoint(points: IPoint[]): IMultiPoint {
    return {
      type: GeometryType.MultiPoint,
      coordinates: points.map((point) => point.coordinates),
    }
  }

  public static isPolygonValid(polygon: IPolygon): boolean {
    return polygon.coordinates.every((points) => points.every((coordinates) => this.areCoordinatesValid(coordinates)))
  }

  public static isPolygonComplete(polygon: IPolygon): boolean {
    return Boolean(
      polygon &&
        polygon.coordinates[0] &&
        polygon.coordinates[0].length >= 3 &&
        deepEqual(polygon.coordinates[0][0], polygon.coordinates[0][polygon.coordinates[0].length - 1]),
    )
  }

  // tolerance is allowed distance (in TURF_UNITS) between point1 and point2 to still be considered equal.
  public static arePointsEqual(point1: IPoint, point2: IPoint, tolerance?: number): boolean {
    if (tolerance) {
      return this.distance(point1, point2) <= tolerance
    }
    return this.geohash(point1) === this.geohash(point2)
  }

  public static geohash(point: IPoint): string {
    /**
     * We use the default ngeohash precision of 9 - a geohash 9 characters long. This is a very fine-grained geohash,
     * it spans only about half the width of a major road. This is important, because we often use geohash for "are
     * these points about equal", and we don't want stops across the street from each other on a major road to be
     * considered equal.
     *
     * An example geohash with precision 9 (i.e. a 9 digit long string): https://geohash.softeng.co/9uftxg1t9
     */
    return ngeohash.encode(point.coordinates[1], point.coordinates[0])
  }

  public static generateRandPointInside(polygon: IPolygon): IPoint {
    const bbox = extent(polygon) as BBox
    let point = randomPoint(1, { bbox })
    while (!this.inside(point.features[0].geometry as IPoint, polygon)) {
      point = randomPoint(1, { bbox })
    }
    return point.features[0].geometry as IPoint
  }

  public static simplify<T extends IGeometry>(geometry: T): T {
    if (JSON.stringify(geometry).length < MINIMUM_SIMPLIFIED_GEOMETRY_SIZE) {
      return geometry
    }

    return simplify(geometry, { tolerance: SIMPLIFICATION_TOLERANCE, highQuality: true })
  }

  public static truncate<T extends IGeometry>(geometry: T, options: { decimalPlaces?: number } = {}): T {
    const turfOptions = {
      precision: options.decimalPlaces,
    }
    const defaultTurfOptions = { precision: 6 }
    return truncate(geometry, { ...defaultTurfOptions, ...turfOptions })
  }

  public static centroid(polygon: IPolygon): IPoint {
    const result = centroid(polygon).geometry

    return {
      type: GeometryType.Point,
      coordinates: result.coordinates as GeoJsonCoordinate,
    }
  }

  /**
   * Calculates the centroid and radius of a list of polygons
   */
  public static centroidOfPolygons(polygons: IPolygon[]): { centroid: IPoint; radius: number } {
    const multiPolygon = turf.multiPolygon(polygons.map((polygon) => polygon.coordinates))
    const bbox = turf.bbox(multiPolygon)
    const centroid = turf.centroid(multiPolygon).geometry as IPoint

    const corners = [
      [bbox[0], bbox[1]], // Bottom left
      [bbox[0], bbox[3]], // Top left
      [bbox[2], bbox[1]], // Bottom right
      [bbox[2], bbox[3]], // Top right
    ]
    let maxDistance = 0
    for (const corner of corners) {
      const distance = this.distance(centroid, buildPoint(corner[0], corner[1]))
      if (distance > maxDistance) {
        maxDistance = distance
      }
    }
    return { centroid, radius: maxDistance }
  }

  public static lineSlice(polyline: ILineString, start?: IPoint, end?: IPoint): ILineString {
    if (polyline.coordinates.length === 0) {
      return polyline
    }
    if (!start) {
      start = {
        type: GeometryType.Point,
        coordinates: polyline.coordinates[0],
      }
    }
    if (!end) {
      end = {
        type: GeometryType.Point,
        coordinates: polyline.coordinates[polyline.coordinates.length - 1],
      }
    }
    return lineSlice(start, end, polyline).geometry as ILineString
  }

  public static polylineLength(polyline: ILineString): number {
    return Math.round(length(turf.feature(polyline), { units: TURF_UNITS }))
  }

  public static nearestPointOnLine(polyline: ILineString, point: IPoint): INearestPointResult {
    try {
      // Check if all coordinates in the line are identical (i.e. it's really just a point)
      const isPoint = polyline.coordinates.every(
        (coord) => coord[0] === polyline.coordinates[0][0] && coord[1] === polyline.coordinates[0][1],
      )

      if (isPoint) {
        // Special case: if the line is a point, return the point itself
        // Otherwise nearestPointOnLine will throw an error: "coordinates must contain numbers" (even with cleanCoords)
        return {
          closestPoint: {
            type: GeometryType.Point,
            coordinates: polyline.coordinates[0],
          },
          index: 0,
          distance: this.distance(point, {
            type: GeometryType.Point,
            coordinates: polyline.coordinates[0],
          }),
        }
      }

      // Note, cleanCoords is necessary to avoid a bug in turf where duplicate points in a line string will
      // result in the error "coordinates must contain numbers": https://github.com/Turfjs/turf/issues/2809
      const result = nearestPointOnLine(turf.cleanCoords(polyline), point, {
        units: TURF_UNITS,
      })
      return {
        closestPoint: result.geometry as IPoint,
        index: result.properties.index,
        distance: result.properties.dist,
      }
    } catch (error) {
      this.logger.error('Error in nearestPointOnLine:', { error, polyline, point })
      throw error
    }
  }

  public static bearing(point1: IPoint, point2: IPoint): number | null {
    return isEqual(point1, point2) ? null : turfBearing(point1, point2)
  }

  public static destination(start: IPoint, distanceM: number, bearing: number): IPoint {
    const feature = turfDestination(start, distanceM, bearing, { units: TURF_UNITS })
    return feature.geometry as IPoint
  }

  public static circle(center: IPoint, radiusM: number): IPolygon {
    const result = circle(center, radiusM / 1000, { units: 'kilometers' })

    return this.convertToSparePolygon(result)
  }

  /**
   * Calculates a buffer for input features for a given radius
   */
  public static buffer<T extends ILineString | IPolygon | IPoint>(
    feature: T,
    radius: number,
    units: 'meters' | 'kilometers' | 'miles' | 'feet',
  ): IPolygon | undefined {
    const result = buffer(feature, radius, { units })
    if (!result) {
      // Only valid members will be returned in the output FeatureCollection
      // i.e., the output collection may have fewer members than the input, or even be empty.
      return undefined
    }

    return result.geometry as IPolygon
  }

  /**
   * Spare Admin do not support MultiPolygon, so this function should be used only for exports (like for GTFS-Flex exports)
   */
  public static unionAllowingMultiPolygon(polygons: Array<IPolygon | IMultiPolygon>): IPolygon | IMultiPolygon | null {
    if (polygons.length === 0) {
      return null
    }
    if (polygons.length === 1) {
      return polygons[0]
    }

    const turfPolygons = polygons.map((polygon) => this.convertToTurfPolygon(polygon))

    const unionPolygon = union(turf.featureCollection<Polygon | MultiPolygon>(turfPolygons))
    if (!unionPolygon) {
      return null
    }
    return this.convertToSparePolygon(unionPolygon)
  }

  /**
   * Spare Admin do not support MultiPolygon, so this function should be the default one for unions
   */
  public static union(polygons: IPolygon[]): IPolygon[] {
    const turfPolygons = polygons.map((polygon) => this.convertToTurfPolygon(this.simplify(polygon)))

    const unionPolygons = union(turf.featureCollection(turfPolygons))
    if (!unionPolygons) {
      return []
    }

    const result = this.convertToSparePolygon(unionPolygons)

    if (result === null) {
      return []
    }

    return result.type === GeometryType.Polygon
      ? [result]
      : result.coordinates.map((coordinates) => ({ type: GeometryType.Polygon, coordinates }))
  }

  public static buildBoundaryFromCoordinates(coordinates: Array<{ lat: number; lon: number }>): IPolygon {
    const feature = turf.convex(
      turf.featureCollection(coordinates.map((coordinate) => turf.feature(buildPoint(coordinate.lon, coordinate.lat)))),
    )

    if (!feature) {
      throw new BadRequestError(() => 'Failed to calculate boundary from the provided list of stops', { coordinates })
    }

    return feature.geometry as IPolygon
  }

  public static difference(polygons: Array<IPolygon | IMultiPolygon>): IPolygon | IMultiPolygon | null {
    if (polygons.length === 0) {
      return null
    }

    if (polygons.length === 1) {
      return polygons[0]
    }

    const turfPolygons = polygons.map((polygon) => this.convertToTurfPolygon(polygon))
    const result = difference(turf.featureCollection(turfPolygons))

    if (!result) {
      return null
    }

    return this.convertToSparePolygon(result)
  }

  public static arePolygonsEqual(polygon1: IPolygon, polygon2: IPolygon): boolean {
    return booleanEqual(polygon1, polygon2)
  }

  private static areCoordinatesValid(coordinates: GeoJsonCoordinate): boolean {
    const [longitude, latitude] = coordinates
    return -180 <= longitude && longitude <= 180 && -90 <= latitude && latitude <= 90
  }

  private static convertToTurfPolygon(polygon: IPolygon | IMultiPolygon): Feature<Polygon | MultiPolygon> {
    // Error is coming from here, try add a try..catch and log exactly what is going on...
    try {
      return polygon.type === GeometryType.Polygon
        ? turf.polygon(polygon.coordinates)
        : turf.multiPolygon(polygon.coordinates)
    } catch (error) {
      this.logger.error('Error converting to turf polygon', { error, polygon })
      throw error
    }
  }

  private static convertToSparePolygon<T extends Polygon | MultiPolygon>(
    feature: Feature<T>,
  ): T extends Polygon ? IPolygon : T extends MultiPolygon ? IMultiPolygon : never {
    return (
      feature.geometry.type === 'Polygon'
        ? { type: GeometryType.Polygon, coordinates: feature.geometry.coordinates }
        : { type: GeometryType.MultiPolygon, coordinates: feature.geometry.coordinates }
    ) as T extends Polygon ? IPolygon : T extends MultiPolygon ? IMultiPolygon : never
  }
}
